import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import {
  Card,
  Table,
  Button,
  Form,
  Select,
  Checkbox,
  DatePicker,
  Input,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Alert,
  Spin,
  Modal,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SecurityScanOutlined,
  UserOutlined
} from '@ant-design/icons';
import DialogCustom from '../../../components/dialog';
import {
  fetchPrivilegiosUsuario,
  createPrivilegio,
  updatePrivilegio,
  deletePrivilegio,
  limpiarPrivilegiosState
} from '../../../actions/privilegios';
import { fetchUsuarioById } from '../../../actions/user';
import { setFlashMessage } from '../../../actions/flashMessage';
import {
  moduloOptions,
  accionOptions,
  getModuloColor,
  getAccionColor,
  validarPrivilegio,
  getResumenPrivilegiosUsuario,
  MODULOS,
  ACCIONES
} from '../../../utils/parsers/privilegios';
import Bread from '../../../components/breadcrumb';
import { routePrivilegiosUsuario } from '../../../utils/routesBread';
import './privilegios.scss';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const PrivilegiosUsuario = () => {
  const { id: usuarioId } = useParams();
  const dispatch = useDispatch();
  
  const { privilegiosUsuario, isLoading, isCreating, isUpdating, isDeleting } = 
    useSelector(state => state.privilegiosReducer);
  const { usuario, isLoading: isLoadingUsuario } = useSelector(state => state.usuarioReducer);
  
  const [modalVisible, setModalVisible] = useState(false);
  const [detalleVisible, setDetalleVisible] = useState(false);
  const [privilegioSeleccionado, setPrivilegioSeleccionado] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });
  const [form] = Form.useForm();

  useEffect(() => {
    if (usuarioId) {
      dispatch(fetchPrivilegiosUsuario(usuarioId));
      dispatch(fetchUsuarioById(usuarioId));
    }
    
    return () => {
      dispatch(limpiarPrivilegiosState());
    };
  }, [usuarioId, dispatch]);

  const resumenPrivilegios = getResumenPrivilegiosUsuario(privilegiosUsuario);

  const abrirModal = (privilegio = null) => {
    setModoEdicion(!!privilegio);
    setPrivilegioSeleccionado(privilegio);
    
    if (privilegio) {
      form.setFieldsValue({
        modulo: privilegio.modulo,
        acciones: privilegio.acciones,
        activo: privilegio.activo,
        vigencia: privilegio.vigencia?.desde && privilegio.vigencia?.hasta 
          ? [moment(privilegio.vigencia.desde), moment(privilegio.vigencia.hasta)]
          : null,
        observaciones: privilegio.observaciones || ''
      });
    } else {
      form.setFieldsValue({
        modulo: undefined,
        acciones: [],
        activo: true,
        vigencia: null,
        observaciones: ''
      });
    }
    
    setModalVisible(true);
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setPrivilegioSeleccionado(null);
    setModoEdicion(false);
    form.resetFields();
  };

  const guardarPrivilegio = async (valores) => {
    console.log('Guardando privilegio con valores:', valores);
    
    try {
      // Validaciones básicas
      if (!valores.modulo) {
        dispatch(setFlashMessage('Debe seleccionar un módulo', 'error'));
        return;
      }
      
      if (!valores.acciones || valores.acciones.length === 0) {
        dispatch(setFlashMessage('Debe seleccionar al menos una acción', 'error'));
        return;
      }

      const privilegioData = {
        usuario: usuarioId,
        modulo: valores.modulo,
        acciones: valores.acciones,
        activo: valores.activo ?? true,
        vigencia: valores.vigencia ? {
          desde: valores.vigencia[0].toISOString(),
          hasta: valores.vigencia[1].toISOString()
        } : {
          desde: new Date().toISOString(),
          hasta: null
        },
        observaciones: valores.observaciones || ''
      };

      console.log('Datos del privilegio a enviar:', privilegioData);

      const errores = validarPrivilegio(privilegioData);
      if (Object.keys(errores).length > 0) {
        console.error('Errores de validación:', errores);
        dispatch(setFlashMessage('Datos inválidos. Por favor revise los campos.', 'error'));
        return;
      }

      if (modoEdicion && privilegioSeleccionado) {
        console.log('Actualizando privilegio existente');
        await dispatch(updatePrivilegio(
          privilegioSeleccionado._id, 
          privilegioData, 
          () => {
            cerrarModal();
            dispatch(fetchPrivilegiosUsuario(usuarioId));
          }
        ));
      } else {
        console.log('Creando nuevo privilegio');
        await dispatch(createPrivilegio(
          privilegioData, 
          () => {
            cerrarModal();
            dispatch(fetchPrivilegiosUsuario(usuarioId));
          }
        ));
      }
    } catch (error) {
      console.error('Error al guardar privilegio:', error);
      dispatch(setFlashMessage('Error al guardar el privilegio. Intente nuevamente.', 'error'));
    }
  };

  const eliminarPrivilegio = async (privilegioId) => {
    try {
      await dispatch(deletePrivilegio(
        privilegioId, 
        () => dispatch(fetchPrivilegiosUsuario(usuarioId))
      ));
    } catch (error) {
      console.error('Error al eliminar privilegio:', error);
    }
  };

  const verDetalle = (privilegio, event) => {
    setPrivilegioSeleccionado(privilegio);
    
    // Capturar la posición del click para la animación
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setModalOrigin({ x: centerX, y: centerY });
    } else {
      setModalOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }
    
    setDetalleVisible(true);
  };

  const columnas = [
    {
      title: 'Módulo',
      dataIndex: 'modulo',
      key: 'modulo',
      render: (modulo) => (
        <Tag color={getModuloColor(modulo)}>
          {MODULOS[modulo] || modulo}
        </Tag>
      )
    },
    {
      title: 'Acciones',
      dataIndex: 'acciones',
      key: 'acciones',
      render: (acciones) => (
        <Space wrap>
          {acciones.map(accion => (
            <Tag key={accion} color={getAccionColor(accion)} size="small">
              {ACCIONES[accion] || accion}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      render: (activo, record) => {
        const esVigente = activo && (!record.vigencia?.hasta || new Date(record.vigencia.hasta) >= new Date());
        return (
          <Tag color={esVigente ? 'green' : 'red'}>
            {esVigente ? 'Vigente' : 'Inactivo'}
          </Tag>
        );
      }
    },
    {
      title: 'Vigencia',
      dataIndex: 'vigencia',
      key: 'vigencia',
      render: (vigencia) => {
        if (!vigencia?.hasta) return <span style={{ color: '#8c8c8c' }}>Sin vencimiento</span>;
        const fechaVencimiento = new Date(vigencia.hasta);
        const esProximo = fechaVencimiento <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        return (
          <span style={{ color: esProximo ? '#fa8c16' : '#8c8c8c' }}>
            {fechaVencimiento.toLocaleDateString()}
          </span>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'acciones_tabla',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => verDetalle(record, e)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => abrirModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Está seguro de eliminar este privilegio?"
            onConfirm={() => eliminarPrivilegio(record._id)}
            okText="Sí"
            cancelText="No"
          >
            <Tooltip title="Eliminar">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={isDeleting}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (isLoading || isLoadingUsuario) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Cargando información del usuario y privilegios...</p>
      </div>
    );
  }

  return (
    <div className="privilegios-container">
      {/* Breadcrumb navigation */}
      <div className="header-nav">
        <Bread routes={routePrivilegiosUsuario} />
      </div>
      
      <div className="privilegios-content">
        {/* Header con información del usuario */}
        <Card className="privilegios-header-card" style={{ marginBottom: '24px' }}>
          <Row align="middle" justify="space-between" className="privilegios-header-row">
            <Col xs={24} sm={18} md={16} lg={18}>
              <div className="privilegios-user-info">
                <UserOutlined className="privilegios-user-icon" />
                <div className="privilegios-user-details">
                  <h2 className="privilegios-user-name">
                    Privilegios de {usuario?.nombre || 'Usuario'}
                  </h2>
                  <p className="privilegios-user-meta">
                    {usuario?.email || 'Email no disponible'} • {usuario?.rol || 'Rol no especificado'}
                  </p>
                  {usuario?.estado && (
                    <Tag color={usuario.estado ? 'green' : 'red'} className="privilegios-user-status">
                      {usuario.estado ? 'Activo' : 'Inactivo'}
                    </Tag>
                  )}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={6} md={8} lg={6} className="privilegios-header-actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => abrirModal()}
                loading={isCreating}
                className="privilegios-add-btn"
                size="large"
                block
              >
                <span className="privilegios-add-text">Agregar Privilegio</span>
                <span className="privilegios-add-text-short">Agregar</span>
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Resumen de privilegios */}
        {Object.keys(resumenPrivilegios).length > 0 && (
          <Card 
            title={<><SecurityScanOutlined /> Resumen de Accesos</>}
            className="privilegios-resumen-card"
            style={{ marginBottom: '24px' }}
          >
            <Row gutter={[16, 16]}>
              {Object.entries(resumenPrivilegios).map(([modulo, acciones]) => (
                <Col key={modulo} xs={24} sm={12} md={8} lg={6}>
                  <div className="privilegios-resumen-item">
                    <div className="privilegios-resumen-modulo">
                      {MODULOS[modulo]}
                    </div>
                    <div className="privilegios-resumen-acciones">
                      {acciones.map(accion => (
                        <Tag key={accion} color={getAccionColor(accion)} size="small">
                          {ACCIONES[accion]}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* Tabla de privilegios */}
        <Card title="Privilegios Detallados" className="privilegios-tabla-card">
          {privilegiosUsuario.length === 0 ? (
            <Alert
              message="Sin privilegios asignados"
              description="Este usuario no tiene privilegios específicos asignados. Haga clic en 'Agregar Privilegio' para otorgar accesos."
              type="info"
              showIcon
            />
          ) : (
            <div className="privilegios-tabla-container">
              {/* Vista desktop */}
              <div className="privilegios-tabla-desktop">
                <Table
                  columns={columnas}
                  dataSource={privilegiosUsuario.map(p => ({ ...p, key: p._id }))}
                  pagination={false}
                  size="small"
                  scroll={{ x: 800 }}
                />
              </div>
              
              {/* Vista móvil */}
              <div className="privilegios-tabla-mobile">
                {privilegiosUsuario.map(privilegio => (
                  <Card key={privilegio._id} className="privilegios-mobile-card" size="small">
                    <div className="privilegios-mobile-header">
                      <Tag color={getModuloColor(privilegio.modulo)} className="privilegios-mobile-modulo">
                        {MODULOS[privilegio.modulo] || privilegio.modulo}
                      </Tag>
                      <div className="privilegios-mobile-estado">
                        <Tag color={privilegio.activo && (!privilegio.vigencia?.hasta || new Date(privilegio.vigencia.hasta) >= new Date()) ? 'green' : 'red'}>
                          {privilegio.activo && (!privilegio.vigencia?.hasta || new Date(privilegio.vigencia.hasta) >= new Date()) ? 'Vigente' : 'Inactivo'}
                        </Tag>
                      </div>
                    </div>
                    
                    <div className="privilegios-mobile-acciones">
                      <strong>Acciones:</strong>
                      <div className="privilegios-mobile-acciones-tags">
                        {privilegio.acciones.map(accion => (
                          <Tag key={accion} color={getAccionColor(accion)} size="small">
                            {ACCIONES[accion] || accion}
                          </Tag>
                        ))}
                      </div>
                    </div>
                    
                    {privilegio.vigencia?.hasta && (
                      <div className="privilegios-mobile-vigencia">
                        <strong>Vigencia:</strong> 
                        <span className={new Date(privilegio.vigencia.hasta) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'privilegios-vigencia-proximo' : 'privilegios-vigencia-texto'}>
                          {new Date(privilegio.vigencia.hasta).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    <div className="privilegios-mobile-actions">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={(e) => verDetalle(privilegio, e)}
                        size="small"
                      >
                        Ver
                      </Button>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => abrirModal(privilegio)}
                        size="small"
                      >
                        Editar
                      </Button>
                      <Popconfirm
                        title="¿Eliminar este privilegio?"
                        onConfirm={() => eliminarPrivilegio(privilegio._id)}
                        okText="Sí"
                        cancelText="No"
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={isDeleting}
                          size="small"
                        >
                          Eliminar
                        </Button>
                      </Popconfirm>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Modal para crear/editar privilegio */}
        <DialogCustom
          open={modalVisible}
          onClose={cerrarModal}
          title={modoEdicion ? 'Editar Privilegio' : 'Agregar Privilegio'}
          maxWidth="md"
        >
          <div style={{ padding: '20px 24px' }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={guardarPrivilegio}
              className="privilegios-modal-form"
            >
              <Form.Item
                name="modulo"
                label="Módulo"
                rules={[{ required: true, message: 'Seleccione un módulo' }]}
              >
                <Select 
                  placeholder="Seleccione un módulo" 
                  size="large"
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {moduloOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="acciones"
                label="Acciones Permitidas"
                rules={[
                  { required: true, message: 'Seleccione al menos una acción' },
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject(new Error('Debe seleccionar al menos una acción'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Checkbox.Group className="privilegios-checkbox-group">
                  <Row gutter={[16, 8]}>
                    {accionOptions.map(option => (
                      <Col key={option.value} xs={24} sm={12} md={8}>
                        <Checkbox value={option.value} className="privilegios-checkbox">
                          {option.label}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                name="vigencia"
                label="Período de Vigencia (opcional)"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value.length === 2) {
                        const [inicio, fin] = value;
                        if (inicio && fin && inicio.isAfter(fin)) {
                          return Promise.reject(new Error('La fecha de inicio no puede ser posterior a la fecha de fin'));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <RangePicker 
                  style={{ width: '100%' }}
                  placeholder={['Fecha inicio', 'Fecha fin']}
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  size="large"
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>

              <Form.Item
                name="observaciones"
                label="Observaciones"
              >
                <TextArea 
                  rows={3} 
                  placeholder="Comentarios adicionales sobre este privilegio..."
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="activo"
                label="Estado del Privilegio"
                valuePropName="checked"
                initialValue={true}
              >
                <Checkbox className="privilegios-checkbox">
                  Privilegio activo
                </Checkbox>
              </Form.Item>

              <Form.Item className="privilegios-modal-actions">
                <Space size="middle">
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={isCreating || isUpdating}
                    size="large"
                    className="privilegios-submit-btn"
                  >
                    {modoEdicion ? 'Actualizar' : 'Crear'} Privilegio
                  </Button>
                  <Button onClick={cerrarModal} size="large">
                    Cancelar
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </DialogCustom>

        {/* Modal de detalle */}
        <Modal
          title="Detalle del Privilegio"
          open={detalleVisible}
          onCancel={() => setDetalleVisible(false)}
          footer={[
            <Button key="cerrar" onClick={() => setDetalleVisible(false)}>
              Cerrar
            </Button>
          ]}
          width={700}
          centered
          destroyOnClose
          className="privilegios-modal-detalle"
          transitionName=""
          maskTransitionName=""
          modalRender={(modal) => (
            <div
              className={detalleVisible ? 'privilegios-modal-enter' : 'privilegios-modal-exit'}
              style={{
                transformOrigin: `${modalOrigin.x - window.innerWidth / 2}px ${modalOrigin.y - window.innerHeight / 2}px`,
              }}
            >
              {modal}
            </div>
          )}
        >
          {privilegioSeleccionado && (
            <div style={{ padding: '8px 0' }}>
              <Descriptions 
                bordered 
                column={{ xs: 1, sm: 2, md: 2 }}
                size="middle"
                labelStyle={{ fontWeight: 600, backgroundColor: '#fafafa' }}
                contentStyle={{ backgroundColor: '#ffffff' }}
              >
                <Descriptions.Item label="Módulo">
                  <Tag color={getModuloColor(privilegioSeleccionado.modulo)}>
                    {MODULOS[privilegioSeleccionado.modulo]}
                  </Tag>
                </Descriptions.Item>
                
                <Descriptions.Item label="Estado">
                  <Tag color={privilegioSeleccionado.activo ? 'green' : 'red'}>
                    {privilegioSeleccionado.activo ? 'Activo' : 'Inactivo'}
                  </Tag>
                </Descriptions.Item>
                
                <Descriptions.Item label="Acciones Permitidas" span={2}>
                  <Space wrap>
                    {privilegioSeleccionado.acciones.map(accion => (
                      <Tag key={accion} color={getAccionColor(accion)}>
                        {ACCIONES[accion]}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
                
                {privilegioSeleccionado.vigencia?.desde && (
                  <Descriptions.Item label="Vigente desde">
                    {new Date(privilegioSeleccionado.vigencia.desde).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Descriptions.Item>
                )}
                
                {privilegioSeleccionado.vigencia?.hasta && (
                  <Descriptions.Item label="Vigente hasta">
                    <span style={{ 
                      color: new Date(privilegioSeleccionado.vigencia.hasta) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? '#fa8c16' : '#595959'
                    }}>
                      {new Date(privilegioSeleccionado.vigencia.hasta).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </Descriptions.Item>
                )}
                
                {!privilegioSeleccionado.vigencia?.hasta && (
                  <Descriptions.Item label="Vigencia" span={privilegioSeleccionado.vigencia?.desde ? 1 : 2}>
                    <span style={{ color: '#8c8c8c' }}>Sin fecha de vencimiento</span>
                  </Descriptions.Item>
                )}
                
                <Descriptions.Item label="Fecha de creación">
                  {new Date(privilegioSeleccionado.createdAt).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Descriptions.Item>
                
                {privilegioSeleccionado.updatedAt && privilegioSeleccionado.updatedAt !== privilegioSeleccionado.createdAt && (
                  <Descriptions.Item label="Última actualización">
                    {new Date(privilegioSeleccionado.updatedAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Descriptions.Item>
                )}
                
                {privilegioSeleccionado.observaciones && (
                  <Descriptions.Item label="Observaciones" span={2}>
                    <div style={{ 
                      backgroundColor: '#f9f9f9',
                      padding: '12px',
                      borderRadius: '6px',
                      borderLeft: '4px solid #1890ff',
                      fontStyle: 'italic'
                    }}>
                      {privilegioSeleccionado.observaciones}
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default PrivilegiosUsuario;
